import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Header from './components/Header';
import classes from './ApplicationLayout.less';

import { withSession } from 'containers/SessionProvider';

import { FormattedMessage } from 'react-intl';
import browserLanguage from 'lib/browserLanguage';
import { colors } from 'styles/provider';
import createFetcher from 'lib/createFetcher';
import { actions as sessionActions } from 'store/session';
import { actions as settingsActions } from 'store/settings';
import getCurrentJWT from 'lib/jwtHandling';
import OrgSelection from './components/OrgSelection';
import LoginInfoDialog from './components/LoginInfoDialog';
import LicenseDialog from './components/LicenseDialog';

const fetchUserOrgs = () => {
  const url = '/api/patient/v2/organizations/list';
  return createFetcher(url, { method: 'GET' });
};
const fetchAllOrgs = () => {
  const url = '/api/vault/v2/organizations';
  return createFetcher(url, { method: 'GET' });
};
const selectOrg = id => {
  const url = '/api/patient/v2/organizations/select' + '/' + id;
  return createFetcher(url, { method: 'PATCH' }).then(res => (res.ok ? res.text() : null));
};
const jwtModify = jmr => {
  const url = '/auth/mystellarq/v0/modify';
  return createFetcher(url, { method: 'PATCH', body: jmr });
};

const ApplicationLayout = ({ session, settings, children, dispatch }) => {
  const lang = session && session.lang ? session.lang : browserLanguage;

  const privacyPolicyUri =
    lang === 'fi' ? 'https://my.stellarq.fi/fi/tietosuojaseloste' : 'https://my.stellarq.fi/en/privacy-policy';

  const [availableOrgs, setAvailableOrgs] = React.useState(null);
  const [allOrgs, setAllOrgs] = React.useState([]);
  const [selectedOrg, setSelectedOrg] = React.useState(null);

  // Show the mobile information dialog on the first opening if patient
  const [firstRefresh, setFirstRefresh] = React.useState(!session?.clorgid && window.__ROLE__ === 'PATIENT');
  const [mobileInfoDialogOpen, setMobileInfoDialogOpen] = React.useState(false);
  const [licenseDialogOpen, setLicenseDialogOpen] = React.useState(false);

  // Show the org selection if not selected before in this session, more than 1 org available and if a patient
  const showOrganizationSelection =
    !session?.clorgid && (availableOrgs || []).length > 1 && window.__ROLE__ === 'PATIENT';

  const recommendApp = settings?.ms?.app?.recommendApplication;

  const onSelectOrg = org => {
    if (org) {
      selectOrg(org).then(jmr => {
        if (jmr) {
          jwtModify(jmr).then(res => {
            if (res) {
              dispatch(sessionActions.setSessionData(getCurrentJWT()));
              dispatch(settingsActions.loadSettings());
            }
          });
        }
      });
    }
  };

  React.useEffect(() => {
    // Fetch orgs for user and all available orgs so that we can map the ids to display names
    !availableOrgs &&
      fetchUserOrgs().then(res => {
        setAvailableOrgs(res || []);
      });
    fetchAllOrgs().then(res => {
      setAllOrgs(res || []);
    });

    if (session?.clorgid) {
      dispatch(settingsActions.loadSettings());
    }
  }, []);

  React.useEffect(() => {
    // If only one available org, select that automatically
    if (availableOrgs && availableOrgs.length === 1 && !session?.clorgid) {
      onSelectOrg(availableOrgs[0]);
    }
  }, [availableOrgs]);

  React.useEffect(() => {
    // On first load of the page, show the mobile app information dialog if it's configured for the organization
    if (recommendApp && firstRefresh) {
      setMobileInfoDialogOpen(true);
    }
  }, [recommendApp]);

  const orgName = allOrgs.find(ao => ao.orgId === session?.clorgid)?.displayName;

  const toggleDialog = () => {
    setMobileInfoDialogOpen(!mobileInfoDialogOpen);
  };

  return availableOrgs === null ? (
    <>{/** Wait for available orgs fetch */}</>
  ) : showOrganizationSelection ? (
    <OrgSelection
      availableOrgs={availableOrgs}
      allOrgs={allOrgs}
      selectedOrg={selectedOrg}
      setSelectedOrg={setSelectedOrg}
      onSelectOrg={onSelectOrg}
    />
  ) : (
    <div className={classes.app}>
      <Header orgName={orgName} toggleDialog={toggleDialog} recommendApp={recommendApp} />
      {session.delegated && (
        <div
          style={{
            display: 'flex',
            flex: 1,
            height: '3rem',
            backgroundColor: colors.clrAccent1,
            color: colors.clrText,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FormattedMessage
            id="delegate.loggedIn"
            values={{ firstName: session.firstName, lastName: session.lastName }}
          />
        </div>
      )}
      {children}
      <div style={{ width: '100%', textAlign: 'center', paddingBottom: '3rem' }}>
        <div>
          <a href={privacyPolicyUri} target="_blank" rel="noreferrer" className={classes.privacyPolicy}>
            <FormattedMessage id="privacystatement.title" />
          </a>
          <div className={classes.privacyPolicy} onClick={() => setLicenseDialogOpen(true)}><FormattedMessage id="dictionary.licenses" /></div>
        </div>
      </div>
      <LoginInfoDialog
        dialogOpen={mobileInfoDialogOpen}
        closeDialog={() => {
          setMobileInfoDialogOpen(false);
          setFirstRefresh(false);
        }}
      />
      <LicenseDialog
        dialogOpen={licenseDialogOpen}
        closeDialog={() => {
          setLicenseDialogOpen(false);
        }}
      />
    </div>
  );
};

ApplicationLayout.propTypes = {
  session: PropTypes.object,
  settings: PropTypes.object,
  children: PropTypes.node,
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = ({ settings = {} }) => ({ settings });

export default connect(mapStateToProps, null)(withSession(ApplicationLayout));
